import type {
  IRuleBucketMemberRef,
  IRuleGroupUpdateRequest,
} from "types/AdjustmentRules/AdjustmentRules";

import { ALL_PAYERS_LABEL } from "./ruleApplicability";

/** The fields of a MPPR / Case Rate / Package row (as loaded by id) that bucket mode reads. */
export interface IBucketableRule {
  groupKey?: string | null;
  /** The server's list of this row's bucket, the row itself included. */
  bucketMembers?: IRuleBucketMemberRef[];
}

export interface IRuleBucketMember {
  id: string;
  /** Null on the provider's "All payers" row (CLE-2842 step 2). */
  payerId: number | null;
  payerName: string;
}

export interface IPayerOption {
  name: string;
  value: number;
}

/** A payer the bucket covers — `value: null` is the provider's All payers row. */
export interface IBucketPayer {
  name: string;
  value: number | null;
}

/**
 * The rows that share `row`'s bucket — the same rule for the same provider, differing only in
 * payer — in the server's order (payer name, then id; the All payers row first). Empty when the
 * row has no groupKey; a lone rule is a bucket of one.
 */
export function toBucketMembers(
  row: IBucketableRule | undefined
): IRuleBucketMember[] {
  if (!row?.groupKey) return [];
  const members = (row.bucketMembers ?? []).map((member) => ({
    id: member.id,
    payerId: member.payerId ?? null,
    payerName:
      member.payer?.name ?? (member.payerId == null ? ALL_PAYERS_LABEL : "—"),
  }));
  // CLE-2842 step 2 — the server already lists the All payers row first; keep it there even if
  // a response does not, so it always leads the chips. The sort is stable, so the rest keep
  // the server's order.
  return [...members].sort(
    (a, b) => Number(a.payerId !== null) - Number(b.payerId !== null)
  );
}

export function buildRuleGroupUpdateRequest<TData>({
  members,
  groupKey,
  data,
  removedIds,
  addedPayerIds,
}: {
  members: IRuleBucketMember[];
  groupKey: string;
  data: TData;
  removedIds: string[];
  addedPayerIds: number[];
}): IRuleGroupUpdateRequest<TData> {
  return {
    ids: members.map((member) => member.id),
    expectedGroupKey: groupKey,
    data,
    // Optional in the contract — only sent when the user actually added or removed a payer.
    ...(addedPayerIds.length ? { addPayerIds: addedPayerIds } : {}),
    ...(removedIds.length ? { removeIds: removedIds } : {}),
  };
}

/** The human message an API error carries (`{ message }`, `{ messages: [] }` or a bare string). */
export function extractServerErrorMessage(error: unknown): string | null {
  if (!error || typeof error !== "object") return null;
  const { data, message } = error as any;
  if (typeof data === "string") return data;
  if (data && typeof data === "object") {
    if (typeof data.message === "string") return data.message;
    if (Array.isArray(data.messages) && typeof data.messages[0] === "string") {
      return data.messages[0];
    }
    if (typeof data.error === "string") return data.error;
  }
  return typeof message === "string" ? message : null;
}

export const payerCountLabel = (count: number) =>
  `${count} payer${count === 1 ? "" : "s"}`;
